import axios from "axios";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/parking";

// Create axios instance with auth header
const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      console.warn("Authentication token expired or invalid");

      // Clear stored tokens
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");

      // Redirect to login page
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const parseDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString);
};

const useParking = () => {
  // Get all parking spots
  const getAllParkingSpots = async () => {
    try {
      const response = await api.get("/spots");
      return response.data;
    } catch (error) {
      console.error("Error fetching parking spots:", error);
      throw error;
    }
  };

  // Get parking spot by ID
  const getParkingSpotById = async (id) => {
    try {
      const response = await api.get(`/spots/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching parking spot:", error);
      throw error;
    }
  };

  const getPointTransfers = async (userId) => {
    try {
      const response = await api.post(`/point-transfers`, { userId });
      console.log("getPointTransfers", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching point transfers:", error);
      throw error;
    }
  };

  // Create reservation
  const createReservation = async (reservationData) => {
    console.log("createReservation", reservationData);
    try {
      // Ensure dates are in ISO format before sending to API
      const formattedData = {
        ...reservationData,
      };

      const response = await api.post("/reservations", formattedData);

      // Parse dates in the response
      return {
        ...response.data,
        start_time: parseDate(response.data.start_time),
        end_time: parseDate(response.data.end_time),
      };
    } catch (error) {
      console.error("Error creating reservation:", error);
      throw error;
    }
  };

  // Get user's reservations
  const getReservations = async (filters = {}) => {
    try {
      const response = await api.get("/reservations", { params: filters });
      return response.data;
    } catch (error) {
      console.error("Error fetching reservations:", error);
      throw error;
    }
  };

  const getAllReservationsOfSpot = async (spotId) => {
    try {
      const response = await api.get(`/spots/${spotId}/reservations`);
      return response.data;
    } catch (error) {
      console.error("Error fetching reservations of spot:", error);
      throw error;
    }
  };

  // Share parking spot
  const shareParking = async (spotId, shareData) => {
    try {
      const response = await api.post(`/spots/${spotId}/share`, shareData);
      return response.data;
    } catch (error) {
      console.error("Error sharing parking spot:", error);
      throw error;
    }
  };

  const cancelReservation = async (reservationId, userId) => {
    try {
      const response = await api.delete(
        `/reservations/${reservationId}?userId=${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error canceling reservation:", error);
      throw error;
    }
  };

  const updateSpotStatus = async (spotId, status) => {
    console.log("updateSpotStatus", spotId, status);
    try {
      const response = await api.patch(`/spots/${spotId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error("Error updating spot status:", error);
      throw error;
    }
  };

  const resetSpotStatus = async (spotId) => {
    try {
      const response = await api.patch(`/spots/${spotId}/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting spot status:", error);
      throw error;
    }
  };

  const setAvailableDates = async (spotId, dates, userId) => {
    console.log("setAvailableDates", spotId, dates, userId);
    const response = await api.post(`/spots/${spotId}/availability`, {
      dates,
      userId,
    });
    return response.data;
  };

  const getAvailableDates = async (spotId) => {
    const response = await api.get(`/spots/${spotId}/availability`);
    return response.data;
  };

  const getReservedDates = async (spotId) => {
    const response = await api.get(`/spots/${spotId}/reservations`);
    return response.data;
  };

  const deleteAvailability = async (availabilityId, userId) => {
    try {
      const response = await api.delete(
        `/availability/${availabilityId}?userId=${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting availability:", error);
      throw error;
    }
  };

  const addParkingSpot = async (spotData) => {
    try {
      const response = await api.post("/spots", spotData);
      return response.data;
    } catch (error) {
      console.error("Error adding parking spot:", error);
      throw error;
    }
  };

  const updateParkingSpot = async (spotData) => {
    try {
      const response = await api.put(`/spots/${spotData.id}`, spotData);
      return response.data;
    } catch (error) {
      console.error("Error updating parking spot:", error);
      throw error;
    }
  };

  const deleteParkingSpot = async (id) => {
    try {
      const response = await api.delete(`/spots/${id}`);
      return response.data;
    } catch (error) {
      console.error(
        "You can't delete this parking spot, it has active reservations"
      );
      throw error;
    }
  };

  const checkAvailability = async ({ startDate, endDate, excludeUserId }) => {
    try {
      const response = await api.post("/check-availability", {
        startDate: startDate,
        endDate: endDate,
        excludeUserId,
      });
      return response.data;
    } catch (error) {
      console.error("Error checking availability:", error);
      throw error;
    }
  };

  return {
    getAllParkingSpots,
    getParkingSpotById,
    createReservation,
    getReservations,
    shareParking,
    cancelReservation,
    addParkingSpot,
    updateParkingSpot,
    deleteParkingSpot,
    updateSpotStatus,
    resetSpotStatus,
    setAvailableDates,
    getAvailableDates,
    deleteAvailability,
    getPointTransfers,
    getReservedDates,
    getAllReservationsOfSpot,
    checkAvailability,
  };
};

export default useParking;
